import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';
import { ProductRecipe, ProductRecipeInsert } from '../../models/product-recipe/product-recipe';
import { ProductRecipeSearch } from '../../models/product-recipe/product-recipe-search';

@Injectable({
  providedIn: 'root',
})
export class ProductRecipeProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.itemApi + '/api/v1/product-recipe';

  public getById(id: string): Observable<ApiResponse<ProductRecipe>> {
    return this.httpClient.get<ApiResponse<ProductRecipe>>(`${this.baseUrl}/${id}`);
  }

  public getByOutputItemCode(itemCode: string): Observable<ApiResponse<ProductRecipe[]>> {
    return this.httpClient.get<ApiResponse<ProductRecipe[]>>(`${this.baseUrl}/by-output/${encodeURIComponent(itemCode)}`);
  }

  public create(recipe: ProductRecipeInsert): Observable<ApiResponse<ProductRecipe>> {
    return this.httpClient.post<ApiResponse<ProductRecipe>>(this.baseUrl, recipe);
  }

  public update(id: string, recipe: ProductRecipe): Observable<ApiResponse<ProductRecipe>> {
    return this.httpClient.put<ApiResponse<ProductRecipe>>(`${this.baseUrl}/${id}`, recipe);
  }

  public updateStatus(id: string, status: number): Observable<ApiResponse<ProductRecipe>> {
    return this.httpClient.patch<ApiResponse<ProductRecipe>>(`${this.baseUrl}/${id}/status`, status);
  }

  public search(
    searchCommand: ProductRecipeSearch,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder?: number,
    matchAny = false,
  ): Observable<ApiResponse<PaginatedApiResponse<ProductRecipe>>> {
    let url = `${this.baseUrl}/search?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      url += `&sortField=${sortField}&sortOrder=${sortOrder ?? -1}`;
    }
    if (matchAny) {
      url += '&matchAny=true';
    }
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<ProductRecipe>>>(url, searchCommand);
  }

  public getByNumber(number: string): Observable<ApiResponse<ProductRecipe>> {
    return this.httpClient.get<ApiResponse<ProductRecipe>>(`${this.baseUrl}/number/${encodeURIComponent(number)}`);
  }

  public delete(id: string): Observable<ApiResponse<void>> {
    return this.httpClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }
}
